
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { allBlogArticles } from '../../../mocks/blogArticles';

interface StateRelatedArticlesProps {
  stateName: string;
  stateSlug: string;
  region: string;
}

const StateRelatedArticles = ({ stateName, stateSlug, region }: StateRelatedArticlesProps) => {
  const navigate = useNavigate();

  const { stateArticles, regionArticles } = useMemo(() => {
    const byState = allBlogArticles.filter(
      (a) => a.stateSlug === stateSlug && a.countrySlug === 'usa'
    );

    const regionStatesMap: Record<string, string[]> = {
      Northeast: ['ny', 'nj', 'pa', 'ma', 'ct', 'ri', 'me', 'nh', 'vt', 'de', 'md', 'dc'],
      Southeast: ['fl', 'ga', 'nc', 'sc', 'va', 'tn', 'al', 'ms', 'la', 'ar', 'ky', 'wv'],
      Midwest: ['il', 'oh', 'mi', 'in', 'wi', 'mn', 'ia', 'mo', 'ks', 'ne', 'nd', 'sd'],
      Southwest: ['tx', 'az', 'nm', 'ok'],
      West: ['co', 'ut', 'nv', 'id', 'mt', 'wy'],
      Pacific: ['ca', 'wa', 'or', 'hi', 'ak'],
    };

    const regionSlugs = regionStatesMap[region] || [];
    const byRegion = allBlogArticles.filter(
      (a) =>
        a.countrySlug === 'usa' &&
        regionSlugs.includes(a.stateSlug) &&
        a.stateSlug !== stateSlug
    );

    return { stateArticles: byState, regionArticles: byRegion };
  }, [stateSlug, region]);

  const displayArticles = useMemo(() => {
    if (stateArticles.length >= 3) return stateArticles.slice(0, 6);
    const combined = [...stateArticles];
    const ids = new Set(combined.map((a) => a.id));
    for (const a of regionArticles) {
      if (!ids.has(a.id)) {
        combined.push(a);
        ids.add(a.id);
      }
      if (combined.length >= 6) break;
    }
    if (combined.length < 3) {
      const featured = allBlogArticles.filter((a) => a.featured && !ids.has(a.id));
      for (const a of featured) {
        combined.push(a);
        ids.add(a.id);
        if (combined.length >= 6) break;
      }
    }
    return combined;
  }, [stateArticles, regionArticles]);

  if (displayArticles.length === 0) return null;

  const hasDirectArticles = stateArticles.length > 0;

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              <strong>Tax Tips &amp; Guides for {stateName}</strong>
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              {hasDirectArticles
                ? `Expert strategies for ${stateName} taxpayers to maximize take-home pay`
                : `Helpful tax articles from the ${region} region and beyond`}
            </p>
          </div>
          <a
            href="/blog"
            onClick={(e) => {
              e.preventDefault();
              navigate('/blog');
            }}
            className="hidden md:flex items-center gap-1 text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            View All Articles <i className="ri-arrow-right-line"></i>
          </a>
        </div>

        {/* State-specific articles */}
        {stateArticles.length > 0 && (
          <div className="mb-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-teal-50 border border-teal-200 rounded-full text-xs font-medium text-teal-700">
              <i className="ri-map-pin-2-fill text-teal-500"></i>
              {stateArticles.length} article{stateArticles.length !== 1 ? 's' : ''} about {stateName}
            </span>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayArticles.map((article) => {
            const isStateMatch = article.stateSlug === stateSlug;
            return (
              <article
                key={article.id}
                className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-lg transition-all duration-300 cursor-pointer hover:-translate-y-1"
              >
                <a
                  href={`/blog/${article.slug}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`/blog/${article.slug}`);
                  }}
                  className="cursor-pointer"
                >
                  <div className="relative w-full h-[180px] overflow-hidden">
                    <img
                      src={article.image}
                      alt={`${article.title} - tax tips for ${article.city}, ${article.state}`}
                      title={`${article.city} ${article.category} guide`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      width="800"
                      height="450"
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3 flex items-center gap-2">
                      <span className="inline-block px-2.5 py-0.5 bg-white/90 backdrop-blur-sm text-gray-800 text-xs font-semibold rounded-full whitespace-nowrap">
                        {article.category}
                      </span>
                      {isStateMatch && (
                        <span className="inline-block px-2.5 py-0.5 bg-teal-500 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                          {stateName}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors leading-snug line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-gray-500 text-sm line-clamp-2 mb-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-400">
                      <div className="flex items-center gap-2">
                        <span>{article.author}</span>
                        <span>&middot;</span>
                        <span>{article.readTime}</span>
                      </div>
                      <span className="flex items-center gap-1 text-teal-500 font-medium">
                        Read <i className="ri-arrow-right-line text-xs"></i>
                      </span>
                    </div>
                  </div>
                </a>
              </article>
            );
          })}
        </div>

        <div className="mt-6 text-center md:hidden">
          <a
            href="/blog"
            onClick={(e) => {
              e.preventDefault();
              navigate('/blog');
            }}
            className="inline-flex items-center gap-1 text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            View All Articles <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </div>
    </section>
  );
};

export default StateRelatedArticles;
